<template>
  <b-modal id="announcement-modal-action" @change="$emit('updateModalState', false)"
    dialog-class="animate-modal-dialog shrink" :visible="visible" hide-header
    centered content-class="announcement-modal-action" size="xl" body-class="modal-body" hide-footer
    modal-class="custom-modal" no-close-on-backdrop>
    <div class="body-container">
      <div class="right-close-panel">
        <button @click="$emit('updateModalState', false)" class="close-button" role="button">
          <img src="@/assets/images/cancel-icon.svg" />
        </button>
      </div>
      <div class="modal-content-wrapper">
        <div class="step-sidebar">
          <div class="step-item" @click="jumpToStep(1)" :class="{ active: currentStep >= 1, completed: currentStep > 1 }">
            <div class="step-circle">1</div>
            <div class="step-content">
              <h3 class="step-title">Basic Information</h3>
              <p class="step-description">Title and message</p>
            </div>
          </div>
          <div class="step-item" @click="jumpToStep(2)" :class="{ active: currentStep >= 2, completed: currentStep > 2 }">
            <div class="step-circle">2</div>
            <div class="step-content">
              <h3 class="step-title">Audience &amp; Schedule</h3>
              <p class="step-description">Who sees it and when</p>
            </div>
          </div>
        </div>

        <div class="main-content">
          <div class="info-container expand">
            <h1 class="modal-title">
              {{ isEditMode ? 'Edit Announcement' : 'Create Announcement' }}
            </h1>

            <div v-show="currentStep === 1" class="step-content">
              <div v-if="isLoadingFormData" class="step-skeleton">
                <div class="form-group my-3">
                  <div class="skeleton-label"></div>
                  <div class="skeleton-input"></div>
                </div>
                <div class="form-group my-3">
                  <div class="skeleton-label"></div>
                  <div class="skeleton-textarea"></div>
                </div>
              </div>
              <div v-else>
                <div class="form-group my-3">
                  <label for="title" class="main-label">Announcement Title (Required)</label>
                  <div class="textfield-wrapper">
                    <input placeholder="Type your announcement title" v-model="title" class="form-input" type="text"
                      id="title" maxlength="255" required>
                    <div class="text-right field-counter">{{ title ? title.length : 0 }}/255</div>
                  </div>
                </div>
                <div class="form-group my-3">
                  <label for="message" class="main-label">Message (Required)</label>
                  <div class="textarea-wrapper">
                    <textarea placeholder="Type your announcement message" v-model="message" class="form-textarea"
                      id="message" rows="6" maxlength="1000"></textarea>
                    <div class="text-right field-counter">{{ message ? message.length : 0 }}/1000</div>
                  </div>
                </div>
              </div>
            </div>

            <div v-show="currentStep === 2" class="step-content">
              <div class="form-group my-3">
                <label class="main-label">Type</label>
                <div class="type-cards">
                  <div class="type-card-container" v-if="!isTrainer">
                    <input type="radio" name="entityType" id="type-global" :value="scopes.Global" v-model="entityType"
                      @change="onTypeChange" />
                    <label class="type-card" for="type-global">
                      <p>Global</p>
                      Shown to everyone across the platform.
                    </label>
                  </div>
                  <div class="type-card-container">
                    <input type="radio" name="entityType" id="type-course" :value="scopes.ProductVariant" v-model="entityType"
                      @change="onTypeChange" />
                    <label class="type-card" for="type-course">
                      <p>Course</p>
                      Shown only to students enrolled in a course.
                    </label>
                  </div>
                </div>
              </div>

              <div class="form-group my-3" v-if="isCourseType">
                <label class="main-label">{{ isEditMode ? 'Select Course (Required)' : 'Select Courses (Required)' }}</label>
                <div class="course-select-wrapper">
                  <i class="fas fa-graduation-cap course-select-icon"></i>
                  <v-select v-if="isEditMode" class="course-select" v-model="entityId" :options="availableProductVariants"
                    :reduce="course => course.id" label="title" placeholder="Search and select a course"
                    :filter-by="courseFilterBy" :loading="isLoadingCourses" :disabled="isLoadingCourses">
                    <template #option="{ id, title }">{{ id }} - {{ title }}</template>
                    <template #selected-option>{{ entityId }} - {{ selectedCourseTitle }}</template>
                  </v-select>
                  <v-select v-else multiple class="course-select" v-model="entityIds" :options="availableProductVariants"
                    :reduce="course => course.id" label="title" placeholder="Search and select courses"
                    :filter-by="courseFilterBy" :loading="isLoadingCourses" :disabled="isLoadingCourses">
                    <template #option="{ id, title }">{{ id }} - {{ title }}</template>
                  </v-select>
                </div>
                <p v-if="isLoadingCourses" class="field-hint">Loading courses…</p>
                <p v-else-if="!availableProductVariants.length" class="field-hint text-danger">
                  No courses are available for announcements.
                </p>
              </div>

              <div class="form-group my-3 row-container">
                <div class="row-item">
                  <label for="start-date" class="main-label">Start Date</label>
                  <input type="datetime-local" id="start-date" class="form-input" :value="toLocalInput(startDate)"
                    @input="e => startDate = fromLocalInput(e.target.value)">
                </div>
                <div class="row-item">
                  <label for="end-date" class="main-label">End Date</label>
                  <input type="datetime-local" id="end-date" class="form-input" :value="toLocalInput(endDate)"
                    @input="e => endDate = fromLocalInput(e.target.value)">
                </div>
              </div>
              <p v-if="isDateRangeInvalid" class="text-danger field-hint">End date must be after the start date.</p>
              <p v-else-if="isEndDateInPast" class="text-danger field-hint">End date is already in the past.</p>
            </div>
          </div>

          <div class="step-btn-container">
            <div class="step-navigation">
              <button v-if="currentStep > 1" :disabled="isSubmitting" @click="previousStep" class="step-btn secondary">Previous</button>
              <button v-if="currentStep < totalSteps" :disabled="!isCurrentStepValid || isLoadingFormData" @click="nextStep" class="step-btn">
                Next
                <img src="@/assets/images/arrow-right.svg" />
              </button>
              <button v-if="currentStep === totalSteps" :disabled="!isFormValid || isSubmitting" @click="handleSubmit"
                class="step-btn">
                <b-spinner v-if="isSubmitting" small></b-spinner>
                {{ isSubmitting ? 'Saving…' : (isEditMode ? 'Update' : 'Create') }}
                <img v-if="!isSubmitting" src="@/assets/images/arrow-right.svg" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import { AnnouncementScope } from '../../../constants/announcement';
import { UserType } from '../../../constants/user';

dayjs.extend(utc);

export default {
  name: 'ManageAnnouncementModal',
  components: { vSelect },
  props: {
    visible: { type: Boolean, default: false },
    isEditMode: { type: Boolean, default: false },
    announcementData: { type: Object, default: null },
  },
  data() {
    return {
      currentStep: 1,
      totalSteps: 2,
      isLoadingFormData: false,
      isLoadingCourses: false,
      isSubmitting: false,
      title: null,
      message: null,
      startDate: null,
      endDate: null,
      entityType: AnnouncementScope.Global,
      entityId: null,
      entityIds: [],
    };
  },
  computed: {
    ...mapGetters('productVariant', ['allProductVariants']),
    ...mapState('user', ['user']),

    scopes() {
      return AnnouncementScope;
    },
    isTrainer() {
      return this.equalsIgnoreCase(this.user?.type || this.user?.role, UserType.TRAINER);
    },
    availableProductVariants() {
      if (!this.isTrainer) return this.allProductVariants;
      return ((this.user && this.user.productVariants) || [])
        .filter(productVariant => this.allowsTrainerAnnouncements(productVariant))
        .map(productVariant => ({
          id: productVariant.id || productVariant.productVariantID,
          title: productVariant.title || productVariant.productVariantTitle,
        }));
    },
    isCourseType() {
      return this.equalsIgnoreCase(this.entityType, AnnouncementScope.ProductVariant);
    },
    isDateRangeInvalid() {
      return !!(this.startDate && this.endDate && dayjs(this.endDate).isBefore(dayjs(this.startDate)));
    },
    isEndDateInPast() {
      return !!(this.endDate && dayjs(this.endDate).isBefore(dayjs()));
    },
    isStep1Valid() {
      return !!(this.title && this.title.trim() && this.message && this.message.trim());
    },
    isStep2Valid() {
      const hasCourse = !this.isCourseType || (this.isEditMode ? !!this.entityId : this.entityIds.length > 0);
      return hasCourse && !this.isDateRangeInvalid && !this.isEndDateInPast;
    },
    isCurrentStepValid() {
      if (this.currentStep === 1) return this.isStep1Valid;
      if (this.currentStep === 2) return this.isStep2Valid;
      return true;
    },
    isFormValid() {
      return this.isStep1Valid && this.isStep2Valid;
    },
    selectedCourseTitle() {
      const course = this.availableProductVariants.find(productVariant => String(productVariant.id) === String(this.entityId));
      return course ? course.title : '';
    },
  },
  watch: {
    visible(isCurrentlyVisible) {
      if (isCurrentlyVisible) this.initialize();
    },
  },
  methods: {
    ...mapActions('announcement', ['createAnnouncement', 'createAnnouncements', 'updateAnnouncement', 'fetchAnnouncementById']),
    ...mapActions('productVariant', ['fetchProductVariants']),
    ...mapActions('user', ['fetchCurrentUser']),

    equalsIgnoreCase(a, b) {
      if (a == null || b == null) return a === b;
      return String(a).toLowerCase() === String(b).toLowerCase();
    },

    allowsTrainerAnnouncements(productVariant) {
      let properties = productVariant.properties || {};
      if (productVariant.productVariantExtraProperties) {
        try {
          properties = JSON.parse(productVariant.productVariantExtraProperties);
        } catch (error) {
          properties = {};
        }
      }
      return String(properties.allowTrainerAnnouncements).toLowerCase() === 'true';
    },

    nextStep() {
      if (this.currentStep < this.totalSteps && this.isCurrentStepValid) this.currentStep++;
    },
    previousStep() {
      if (this.currentStep > 1) this.currentStep--;
    },
    jumpToStep(stepNumber) {
      if (stepNumber === 1) this.currentStep = 1;
      else if (stepNumber === 2 && this.isStep1Valid) this.currentStep = 2;
    },

    onTypeChange() {
      if (this.isCourseType) {
        this.loadCourses();
      } else {
        this.entityId = null;
        this.entityIds = [];
      }
    },
    async loadCourses() {
      this.isLoadingCourses = true;
      try {
        if (this.isTrainer) {
          if (!this.user || !this.user.productVariants || !this.user.productVariants.length) {
            await this.fetchCurrentUser();
          }
          return;
        }
        if (!this.allProductVariants.length) await this.fetchProductVariants();
      } finally {
        this.isLoadingCourses = false;
      }
    },
    courseFilterBy(option, label, search) {
      const term = (search || '').toLowerCase();
      return String(option.id).toLowerCase().includes(term) || String(option.title || '').toLowerCase().includes(term);
    },

    toLocalInput(date) {
      return date ? dayjs(date).format('YYYY-MM-DDTHH:mm') : null;
    },
    fromLocalInput(value) {
      return value ? dayjs(value).toISOString() : null;
    },

    async populateFormData() {
      if (!this.isEditMode || !this.announcementData) return;
      this.isLoadingFormData = true;
      try {
        const announcement = await this.fetchAnnouncementById(this.announcementData.id) || this.announcementData;
        this.title = announcement.title;
        this.message = announcement.message;
        this.startDate = announcement.startDate ? dayjs(announcement.startDate).toISOString() : null;
        this.endDate = announcement.endDate ? dayjs(announcement.endDate).toISOString() : null;
        this.entityType = this.equalsIgnoreCase(announcement.entityType, AnnouncementScope.ProductVariant) ? AnnouncementScope.ProductVariant : AnnouncementScope.Global;
        this.entityId = announcement.entityID || null;
      } catch (error) {
        this.showError(error?.message || 'Failed to load the announcement.');
      } finally {
        this.isLoadingFormData = false;
      }
    },

    async handleSubmit() {
      if (!this.isFormValid || this.isSubmitting) return;
      this.isSubmitting = true;

      const startDate = this.startDate ? dayjs(this.startDate).utc().format() : null;
      const endDate = this.endDate ? dayjs(this.endDate).utc().format() : null;

      try {
        if (this.isEditMode) {
          await this.updateAnnouncement({
            announcementId: this.announcementData.id,
            announcementData: {
              title: this.title,
              message: this.message,
              startDate,
              endDate,
              entityType: this.isCourseType ? AnnouncementScope.ProductVariant : null,
              entityId: this.isCourseType ? Number(this.entityId) : null,
            },
          });
          this.showSuccess('Announcement updated successfully.');
        } else if (this.isCourseType) {
          const created = await this.createAnnouncements({
            announcements: this.entityIds.map(id => ({
              title: this.title,
              message: this.message,
              startDate,
              endDate,
              entityType: AnnouncementScope.ProductVariant,
              entityId: Number(id),
            })),
          });
          this.showSuccess(`${(created && created.length) || this.entityIds.length} announcements created successfully.`);
        } else {
          await this.createAnnouncement({
            title: this.title,
            message: this.message,
            startDate,
            endDate,
            entityType: null,
            entityId: null,
          });
          this.showSuccess('Announcement created successfully.');
        }
        this.closeModal();
      } catch (error) {
        this.showError(error?.message || 'Failed to save the announcement.');
      } finally {
        this.isSubmitting = false;
      }
    },

    closeModal() {
      this.$emit('updateModalState', false);
      if (this.isEditMode) this.$emit('editComplete');
      this.resetModel();
    },
    resetModel() {
      Object.assign(this.$data, this.$options.data.call(this));
    },
    async initialize() {
      this.resetModel();
      if (this.isTrainer && !this.isEditMode) this.entityType = AnnouncementScope.ProductVariant;
      if (this.isCourseType || this.isEditMode) await this.loadCourses();
      await this.populateFormData();
    },

    showError(message) {
      this.$toasted.error(message, { icon: 'exclamation-triangle', theme: 'outline', position: 'bottom-center', singleton: true, duration: 3000 });
    },
    showSuccess(message) {
      this.$toasted.success(message, { icon: 'check', theme: 'outline', position: 'bottom-center', duration: 3000 });
    },
  },
};
</script>

<style>
.announcement-modal-action .modal-body.modal-body {
  display: flex;
  flex-direction: row;
  padding: 0 !important;
  overflow: hidden;
}

.announcement-modal-action .modal-body.modal-body .body-container {
  padding: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
}
</style>

<style scoped>
@keyframes skeleton-loading {
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
}

.skeleton-label,
.skeleton-input,
.skeleton-textarea {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-loading 1.5s infinite;
  border-radius: 4px;
}

.skeleton-label { height: 16px; width: 150px; margin-bottom: 8px; }
.skeleton-input { height: 43px; width: 100%; margin-bottom: 20px; }
.skeleton-textarea { height: 140px; width: 100%; margin-bottom: 20px; }

.modal-content-wrapper {
  display: flex;
  gap: 0;
  flex: 1;
}

.step-sidebar {
  display: flex;
  width: 300px;
  flex-shrink: 0;
  background: #f5f5f5;
  padding: 2.5rem 0;
  flex-direction: column;
  justify-content: center;
}

.step-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 0;
  border-left: 2px solid #e5e7eb;
  padding-left: 2rem;
  margin: 1rem 0;
  cursor: pointer;
}

.step-item.active,
.step-item.completed {
  border-left-color: var(--primary-color);
}

.step-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e5e7eb;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
  flex-shrink: 0;
}

.step-item.active .step-circle,
.step-item.completed .step-circle {
  background: var(--primary-color);
  color: white;
}

.step-content {
  padding: 0 2rem;
}

.step-title {
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.25rem 0;
}

.step-item.active .step-title {
  color: var(--primary-color);
}

.step-description {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.right-close-panel {
  position: absolute;
  top: 2.5rem;
  right: 2rem;
  display: flex;
  z-index: 1;
}

.right-close-panel .close-button {
  position: initial;
}

.info-container.expand .step-content {
  height: 60vh;
  overflow: auto;
}

.modal-title {
  color: #000;
  font-family: "Circular Std";
  font-size: 2rem;
  font-weight: 450;
  line-height: 1.5rem;
  margin: 3.5rem 2rem 2rem;
  text-transform: capitalize;
}

.info-container .main-label {
  font-weight: 500;
  font-size: 1rem;
  line-height: 1.37;
  text-transform: uppercase;
  color: #727272;
  margin-bottom: 1rem;
}

.form-input {
  border: 0;
  border-bottom: 1.5px solid #b2b2b2;
  font-size: 1rem;
  outline: none;
  padding: 0.875rem 0;
  width: 100%;
}

.form-input:focus {
  border-color: var(--primary-color) !important;
}

.form-textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
}

.form-textarea:focus {
  border-color: var(--primary-color);
  outline: none;
}

.form-group {
  display: flex;
  flex-direction: column;
  margin-top: 20px;
}

.row-container {
  display: flex;
  gap: 16px;
  flex-direction: row !important;
}

.row-item {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.field-counter {
  text-align: right;
  font-size: 0.8rem;
  color: #888;
  margin-top: 4px;
}

.field-hint {
  font-size: 0.85rem;
  margin-top: 8px;
}

.type-cards {
  display: flex;
  column-gap: 1.5rem;
}

.type-cards input[type="radio"] {
  opacity: 0;
  position: fixed;
  width: 0;
}

.type-cards input[type="radio"]:checked + label {
  border-color: var(--primary-color);
}

.type-cards input[type="radio"]:checked + label::after {
  content: url("~@/assets/images/check-icon.svg");
  position: absolute;
  top: -0.5rem;
  right: -0.5rem;
  background-color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.625rem;
  height: 1.625rem;
  border-radius: 50%;
}

.type-card {
  background: #ffffff;
  border: 1px solid #dcdcdc;
  border-radius: 6px;
  padding: 1.125rem;
  cursor: pointer;
  flex: 1;
  max-width: 16rem;
  font-size: 0.9rem;
  line-height: 1.37;
  color: #727272;
  position: relative;
}

.type-card p {
  color: #000000;
  font-weight: 600;
  font-size: 1.05rem;
  margin-bottom: 0.4rem;
}

.course-select-wrapper {
  position: relative;
  margin-bottom: 0.75rem;
}

.course-select-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  z-index: 1;
  pointer-events: none;
}

.course-select >>> .vs__dropdown-toggle {
  border: 1px solid #d8d8d8;
  border-radius: 8px;
  padding: 7px 10px 7px 34px;
  background: #fff;
  min-height: 44px;
}

.course-select >>> .vs__dropdown-toggle:hover {
  border-color: #b0b0b0;
}

.course-select >>> .vs__search,
.course-select >>> .vs__search:focus {
  font-size: 0.9rem;
}

.course-select >>> .vs__search::placeholder {
  color: #9ca3af;
}

.course-select >>> .vs__selected {
  font-size: 0.9rem;
  color: #111827;
}

.course-select >>> .vs__open-indicator {
  fill: #9ca3af;
}

.course-select >>> .vs__dropdown-menu {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  padding: 6px;
  margin-top: 10px;
  max-height: 260px;
}

.course-select >>> .vs__dropdown-option {
  padding: 9px 12px;
  border-radius: 6px;
  white-space: normal;
  line-height: 1.35;
  font-size: 0.9rem;
  color: #374151;
}

.course-select >>> .vs__dropdown-option--highlight {
  background: #f3f4f6;
  color: #111827;
}

.course-select >>> .vs__no-options {
  padding: 12px;
  color: #9ca3af;
  font-size: 0.9rem;
}

.step-navigation {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 1rem;
  width: 100%;
  padding: 2rem;
}

.step-btn {
  border: 1px solid var(--primary-color);
  border-radius: 6px;
  padding: 10px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  background: #ffffff;
  color: #000000;
  text-transform: uppercase;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.step-btn:not(:disabled):hover {
  background-color: var(--primary-color);
  color: #ffffff;
}

.step-btn:not(:disabled):hover img {
  filter: brightness(0) invert(1);
}

.step-btn:disabled {
  border-color: #ccc;
  color: #999;
  cursor: not-allowed;
}

.step-btn.secondary {
  border-color: var(--border-color);
  color: #374151;
}

.step-btn.secondary:hover {
  background-color: var(--gray-light-hover);
  color: #374151;
}

@media (max-width: 768px) {
  .step-sidebar { display: none; }
}
</style>
